import { isMobile, isTablet } from "react-device-detect";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowUp, LockIcon, PencilLine } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import TextareaAutosize from "react-textarea-autosize";
import {
  useActiveSubscription,
  useGetCounterpartyInfo,
  useGetUserProfile,
  useSendMessage,
} from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import ContactShareButton from "./ContactShareButton";
import { SubscriptionStatus } from "@/generated-api";
import { useTranslation } from "@/hooks/useTranslation";
import { useNavigate } from "react-router-dom";
import { useHaptics } from "@/hooks/useHaptics";

export default function MessageInputBar({
  counterpartyId,
}: {
  counterpartyId: string;
}) {
  const draftKey = `chatDraft:${counterpartyId}`;
  const [message, setMessage] = useState(
    () => sessionStorage.getItem(draftKey) ?? "",
  );
  const { toast } = useToast();
  const { simpleTrigger } = useHaptics();
  const [inputFocused, setInputFocused] = useState(false);
  const increasePadding = inputFocused && (isMobile || isTablet);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const counterpartyQuery = useGetCounterpartyInfo(counterpartyId);
  const isSupport = counterpartyQuery.data?.is_support ?? false;
  const newMessageMutation = useSendMessage(isSupport);
  const userProfileQuery = useGetUserProfile();
  const activeSubscriptionQuery = useActiveSubscription();
  const navigate = useNavigate();
  const t = useTranslation();

  // Reload the draft when switching conversations without unmounting (desktop)
  useEffect(() => {
    setMessage(sessionStorage.getItem(`chatDraft:${counterpartyId}`) ?? "");
  }, [counterpartyId]);

  const updateMessage = (value: string) => {
    setMessage(value);
    if (value) {
      sessionStorage.setItem(draftKey, value);
    } else {
      sessionStorage.removeItem(draftKey);
    }
  };

  const handleSendMessage = () => {
    if (!message.trim() || message.length > 250) return;
    const prevContent = message;
    updateMessage("");
    simpleTrigger();
    newMessageMutation.mutate(
      {
        counterparty_id: counterpartyId,
        content: message,
      },
      {
        onError: (error) => {
          updateMessage(prevContent);
          console.error("Error - could not message user", error);
          toast({
            variant: "destructive",
            title: t.MESSAGES.ERRORS.SEND,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      },
    );
  };

  const subscriptionStatus = activeSubscriptionQuery.data?.subscription_status;

  if (
    (subscriptionStatus === SubscriptionStatus.INACTIVE ||
      subscriptionStatus === SubscriptionStatus.EXPIRED ||
      activeSubscriptionQuery.isError) &&
    !isSupport
  ) {
    return (
      <div
        className={`w-full pt-2 px-2 bg-zinc-900 border-t border-zinc-800 ${
          isDesktop
            ? "pb-2 rounded-br-2xl"
            : increasePadding
              ? "pb-8"
              : "pb-[calc(env(safe-area-inset-bottom)+1rem)]"
        }`}
      >
        <div className="flex items-start gap-x-1 pl-1">
          <TextareaAutosize
            minRows={1}
            maxRows={6}
            maxLength={250}
            disabled={true}
            placeholder={
              activeSubscriptionQuery.isError
                ? t.MESSAGE_DRAWER.ERROR.MEMBERSHIP
                : t.MESSAGES.LABELS.UPGRADE_TO_READ_MESSAGE
            }
            className={`ml-1 min-w-40 flex-1 px-2 py-1 bg-black text-base text-white 
              resize-none outline-none rounded-2xl 
              ${activeSubscriptionQuery.isError && "placeholder:text-red-500"}`}
          />
          <Button
            className="ml-2 dark h-8 rounded-full relative before:absolute before:inset-[-8px] before:content-['']"
            onClick={() => navigate("/upgrade")}
          >
            <LockIcon className="h-4 w-4 mr-1" />
            {t.MESSAGE_DRAWER.LABELS.UPGRADE}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full pt-2 px-2 bg-zinc-900 border-t border-zinc-800 ${
        isDesktop
          ? "pb-2 rounded-br-2xl"
          : increasePadding
            ? "pb-8"
            : "pb-[calc(env(safe-area-inset-bottom)+1rem)]"
      }`}
    >
      <div className="flex items-start gap-x-1 pl-1">
        {!isSupport && <ContactShareButton counterpartyId={counterpartyId} />}
        {userProfileQuery.data?.intro !== null && (
          <Button
            onClick={() => updateMessage(userProfileQuery.data?.intro || "")}
            variant="ghost"
            className="rounded-full dark h-8 w-8 p-0 shrink-0 select-none"
          >
            <PencilLine className="h-5 w-5 text-zinc-400" strokeWidth={2.5} />
          </Button>
        )}
        <TextareaAutosize
          minRows={1}
          maxRows={6}
          maxLength={250}
          value={message}
          onChange={(e) => updateMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && isDesktop) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          placeholder={t.MESSAGES.LABELS.MESSAGE}
          className="ml-1 min-w-40 flex-1 px-2 py-1 bg-black text-base text-white resize-none outline-none rounded-2xl"
        />
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSendMessage();
          }}
          className="ml-2 rounded-full dark h-8 w-8 p-0 shrink-0 relative before:absolute before:inset-[-8px] before:content-['']"
          disabled={
            newMessageMutation.isPending ||
            message.length === 0 ||
            newMessageMutation.isPending
          }
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
